import { BandChip } from "@/components/terminal/band-mark"
import { DisclosureChevron } from "@/components/terminal/disclosure-chevron"
import { AbsenceDash, ReadingTable } from "@/components/terminal/reading-table"
import {
  type AssessmentBand,
  BAND_SPECTRUM,
  dataByAssessmentBand,
} from "@/constants/terminal/assessment-band"

// The band spectrum as one reading table, worst first with UNCLEAR between
// WATCH and STABLE: each band's chip, score range and definition. One piece
// for every place that tables the bands (the assessment legend, Definitions,
// the Policy 2.0 walkthrough). Given a current band, it marks that band and
// dims the rest; without one, every band reads alike. Never pinned: no
// caller scrolls it in a view frame.
export const BandSpectrumTable = ({
  currentBand,
}: {
  currentBand?: AssessmentBand
}) => {
  const isMarked = currentBand !== undefined
  return (
    <ReadingTable
      caption="Assessment spectrum"
      isPinned={false}
      columns={[
        {
          key: "assessment",
          label: "Assessment",
          role: "identity",
          // The head starts where the chip does, past the marker slot.
          headClassName: isMarked
            ? "first:pl-[calc(var(--terminal-row-inset)+1.25rem)]"
            : undefined,
          render: (band) =>
            isMarked ? (
              <span className="flex items-center gap-3">
                <span className="text-foreground w-2 shrink-0 text-center">
                  {band === currentBand && <DisclosureChevron isOpen={false} />}
                </span>
                <BandChip band={band} className="shrink-0" />
              </span>
            ) : (
              <BandChip band={band} className="shrink-0" />
            ),
        },
        {
          key: "score",
          label: "Score",
          role: "numeric",
          render: (band) =>
            dataByAssessmentBand[band].scoreRange ? (
              <span className="type-data whitespace-nowrap">
                {dataByAssessmentBand[band].scoreRange}
              </span>
            ) : (
              <AbsenceDash />
            ),
        },
      ]}
      grow={{
        key: "definition",
        label: "Definition",
        lines: "all",
        cellClassName: () => "type-narrative",
        render: (band) => dataByAssessmentBand[band].definition,
      }}
      rows={BAND_SPECTRUM}
      rowKey={(band) => band}
      rowClassName={(band) => {
        if (!isMarked) return undefined
        return band === currentBand
          ? "bg-popover hover:bg-popover"
          : "opacity-60"
      }}
    />
  )
}
